import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Spinner } from "@/components/ui/spinner";
import type { CoverFrame } from "shared/types/media";
import { THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from "./const";
import {
  getOrCreateVideoBlob,
  getVideoPreviewImageWithRetry,
  revokeBlobUrl,
} from "./utils";
import { Button } from "@/components/ui/button";
import type { LazyBlobRef } from "./types";

export const VideoFrameSelector = ({
  media,
  isLoading,
  onChange,
  onClose,
  videoUrl,
  thumbnails,
  coverFrames,
  videoDurationFromMeta,
  lazyBlobRef,
}: {
  media: File | null;
  isLoading: boolean;
  onChange: (cover: string, timestampInMs: number) => void;
  onClose: () => void;
  videoUrl: string;
  thumbnails: string[];
  coverFrames?: CoverFrame[];
  videoDurationFromMeta?: number;
  lazyBlobRef?: LazyBlobRef;
}) => {
  const [, setVideo] = useState<File | null>(null);
  const [videoThumb, setVideoThumb] = useState<string>("");
  const [sliderX, setSliderX] = useState<number | null>(null);
  const [duration, setDuration] = useState<number>(0); // in second;
  const [videoDuration, setVideoDuration] = useState<number>(0);

  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoThumbRef = useRef("");
  const extractionIdRef = useRef(0);
  const mountedRef = useRef(true);

  // Revoke previous blob URL when videoThumb changes
  useEffect(() => {
    const prev = videoThumbRef.current;
    if (prev && prev !== videoThumb) {
      revokeBlobUrl(prev);
    }
    videoThumbRef.current = videoThumb;
  }, [videoThumb]);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      revokeBlobUrl(videoThumbRef.current);
    };
  }, []);

  useEffect(() => {
    if (videoDurationFromMeta != null && videoDurationFromMeta > 0) {
      // Use backend-provided duration — no need to load a File blob
      setDuration(videoDurationFromMeta);
    } else if (media) {
      setFile(media);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDrag = (e: React.MouseEvent | React.TouchEvent): void => {
    isDragging.current = true;
    updateValue(e);
  };

  const stopDrag = async (): Promise<void> => {
    // Deduplicate: onMouseUp and onMouseLeave can both fire
    if (!isDragging.current) return;
    isDragging.current = false;
    // Allow 0-second frame selection (videoDuration >= 0, not just truthy)
    if (videoUrl && videoDuration >= 0) {
      const requestId = ++extractionIdRef.current;
      try {
        const thumb = await getVideoPreviewImageWithRetry(
          videoUrl,
          videoDuration
        );
        // Discard stale or post-unmount result
        if (requestId !== extractionIdRef.current || !mountedRef.current) {
          revokeBlobUrl(thumb);
          return;
        }
        setVideoThumb(thumb);
      } catch {
        // Remote extraction failed (CORS, codec, etc.)
        // On the coverFrames path, try lazy blob as fallback
        if (
          requestId === extractionIdRef.current &&
          mountedRef.current &&
          lazyBlobRef
        ) {
          try {
            const { blobUrl } = await getOrCreateVideoBlob(
              videoUrl,
              lazyBlobRef
            );
            if (requestId !== extractionIdRef.current || !mountedRef.current) {
              return;
            }
            const thumb = await getVideoPreviewImageWithRetry(
              blobUrl,
              videoDuration
            );
            if (requestId !== extractionIdRef.current || !mountedRef.current) {
              revokeBlobUrl(thumb);
              return;
            }
            setVideoThumb(thumb);
          } catch {
            if (requestId === extractionIdRef.current) {
              console.error("Failed to extract video frame from blob fallback");
            }
          }
        } else if (requestId === extractionIdRef.current) {
          console.error("Failed to extract video frame");
        }
      }
    }
  };

  const updateValue = (e: React.MouseEvent | React.TouchEvent): void => {
    const { left, width } = sliderRef?.current?.getBoundingClientRect() || {};

    // Use explicit undefined checks to allow left=0 (when slider is flush to viewport)
    if (!isDragging.current || left === undefined || !width) return;

    let clientX = 0;
    clientX = "touches" in e ? e.touches[0].clientX : e.clientX;

    if (clientX - left + 2 - THUMBNAIL_WIDTH / 2 <= 0) {
      setVideoDuration(0);
      return;
    }
    if (clientX - left - 2 + THUMBNAIL_WIDTH / 2 >= width) {
      setVideoDuration(duration);
      return;
    }

    const newValue = Math.max(0, Math.min(1, (clientX - left) / width)) * 100;
    setVideoDuration((newValue * duration) / 100);
    setSliderX(newValue);
  };

  const setFile = async (file: File) => {
    setVideo(null);
    setVideoThumb("");
    setSliderX(null);
    setVideoDuration(0);

    const video = document.createElement("video");
    video.preload = "metadata";
    video.addEventListener("loadedmetadata", function () {
      window.URL.revokeObjectURL(video.src);
      setDuration(video.duration);
    });

    video.src = URL.createObjectURL(file);
    videoRef.current = video;

    setVideo(file);
  };

  const onCoverSelect = () => {
    // Transfer ownership of the blob URL to the parent — clear the ref
    // so unmount cleanup won't revoke a URL the parent still needs.
    videoThumbRef.current = "";
    onChange?.(videoThumb, Math.floor(videoDuration * 1000));
    onClose?.();
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = videoDuration;
    }
  }, [videoDuration]);

  if (isLoading || thumbnails.length === 0) {
    return <Spinner size="lg" data-testid="loading-spinner" />;
  }

  return (
    <VideoFrameSelectorWrap>
      <FrameWrap $thumbCount={thumbnails.length}>
        <IndicatorWrap
          ref={sliderRef}
          data-testid="video-slider"
          onMouseDown={startDrag}
          onMouseMove={updateValue}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          onTouchStart={startDrag}
          onTouchMove={updateValue}
          onTouchEnd={stopDrag}
        >
          <Indicator
            style={{
              left:
                sliderX !== null ? `${sliderX}%` : `${THUMBNAIL_WIDTH / 2}px`,
            }}
          >
            <video
              src={videoUrl}
              height={THUMBNAIL_HEIGHT}
              ref={(el) => (videoRef.current = el)}
            />
          </Indicator>
        </IndicatorWrap>
        <Thumbnails $thumbCount={thumbnails.length}>
          {thumbnails.map((item, key) => {
            return (
              <Thumbnail key={key}>
                <img
                  src={item}
                  onClick={() => {
                    setVideoThumb(item);
                    // thumbnails are derived from coverFrames in the same order,
                    // so matching by index keeps the slider timestamp aligned.
                    if (coverFrames?.[key]) {
                      const ts = coverFrames[key].timestampMs / 1000;
                      setVideoDuration(ts);
                      if (duration > 0) {
                        setSliderX((ts / duration) * 100);
                      }
                    }
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </Thumbnail>
            );
          })}
        </Thumbnails>
      </FrameWrap>
      <FrameActionWrap>
        <Button
          variant="blue-primary"
          size="sm"
          onClick={onCoverSelect}
          disabled={videoThumb === ""}
        >
          Done
        </Button>
        <Button variant="blue-tertiary" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </FrameActionWrap>
    </VideoFrameSelectorWrap>
  );
};

const VideoFrameSelectorWrap = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FrameWrap = styled.div<{ $thumbCount: number }>`
  position: relative;
  width: ${({ $thumbCount }) => THUMBNAIL_WIDTH * ($thumbCount || 10)}px;
`;

const FrameActionWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const Thumbnails = styled.div<{ $thumbCount: number }>`
  width: ${({ $thumbCount }) => THUMBNAIL_WIDTH * ($thumbCount || 10)}px;
  display: flex;
  justify-content: flex-start;
  border-radius: 8px;
  border: 1px solid var(--Neutral-N100, #e6e6e6);
  height: ${THUMBNAIL_HEIGHT}px;
  overflow: hidden;
`;

const Thumbnail = styled.div`
  width: ${THUMBNAIL_WIDTH}px;
  height: ${THUMBNAIL_HEIGHT}px;

  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    height: ${THUMBNAIL_HEIGHT}px;
    object-fit: cover;
  }
`;

const IndicatorWrap = styled.div`
  width: 100%;
  height: ${THUMBNAIL_HEIGHT}px;
  position: absolute;
  left: 0;
  background: rgba(255, 255, 255, 0.5);
`;

const Indicator = styled.div`
  width: ${THUMBNAIL_WIDTH}px;
  height: ${THUMBNAIL_HEIGHT}px;
  background: transparent;
  border: 3px solid #ddd;
  position: relative;
  left: 0;
  cursor: pointer;
  background: blue;
  transform: translateX(-50%);
  border-radius: 4.125px;
  border: 4px solid var(--Primary-P500, #4f31ff);
  background: var(--Text-T01, #333);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;

  video {
    width: 100%;
    max-width: 100%;
  }
`;
